/*  ProductViewsWidget – daily product-view counts  */
import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';

import {RootState} from 'appredux/reducers';
import useSupplierAnalytics from '../useSupplierAnalytics';

import './ProductViewsWidget.scss';

/* ---------- helpers ---------- */
const rangeLabel = (f: Date, t: Date) => {
  const df = dayjs(f);
  const dt = dayjs(t);
  if (df.isSame(dt, 'day')) return df.format('DD MMM YYYY');
  if (df.year() === dt.year())
    return `${df.format('DD MMM')} – ${dt.format('DD MMM YYYY')}`;
  return `${df.format('DD MMM YYYY')} – ${dt.format('DD MMM YYYY')}`;
};

const PVTooltip = ({active, payload, label}: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="pv-tooltip">
      <strong>{dayjs(label).format('ddd, DD MMM YYYY')}</strong>
      <br />
      {payload[0].value.toLocaleString()} views
    </div>
  );
};

/* ---------- main component ---------- */
export default function ProductViewsWidget(): React.ReactElement {
  const {start, end} = useSelector((s: RootState) => s.rdwDateFilter);
  const brandId = Number(useSelector((s: RootState) => s.user?.brand?.id ?? 0));
  const {loading, analytics} = useSupplierAnalytics(brandId, start, end);

  /* skeleton while loading */
  if (loading) {
    return <Skeleton variant="rect" height={160} style={{borderRadius: 8}} />;
  }

  if (!analytics) {
    return (
      <Box className="product-views-widget">
        <Typography className="widget-heading">PRODUCT VIEWS</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load product views.
        </Typography>
      </Box>
    );
  }

  /* pick the correct field -- APIs aren’t always consistent */
  const raw =
    analytics.productViews ??
    analytics.productViewsData ??
    (analytics as any).productViewData ??
    [];

  /* normalise + light down-sample (≤ 90 pts) */
  type Point = {date: string; views: number};
  const norm: Point[] = raw.map((d: any) => ({
    date: d.date,
    views:
      d.count ??
      d.numberOfViews ??
      d.productViews ??
      d.storeViews ??
      d.views ??
      0,
  }));

  const step = Math.max(1, Math.ceil(norm.length / 90));
  const data = norm.filter((__, i) => i % step === 0); // <— index param inferred as number

  const hasViews = data.some((r) => r.views > 0);

  return (
    <Box className="product-views-widget">
      <Box className="widget-head">
        <Typography className="widget-heading">PRODUCT VIEWS</Typography>
        <Typography className="widget-sub" variant="caption">
          {rangeLabel(start, end)}
        </Typography>
      </Box>

      <ResponsiveContainer width="95%" height={160}>
        <LineChart data={data} margin={{top: 10, right: 0, left: 0, bottom: 0}}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => dayjs(d).format('DD MMM')}
            angle={-15}
            interval="preserveStartEnd"
            height={38}
            tick={{fontSize: 12, fontWeight: 400, color: '#79716B'}}
          />
          <YAxis
            domain={[
              (min: number) => Math.max(0, min - 2),
              (max: number) => max + 2,
            ]}
            allowDecimals={false}
            tick={{fontSize: 12, fontWeight: 400, color: '#79716B'}}
            width={28}
          />
          <Tooltip content={<PVTooltip />} />
          <Line
            type="monotone"
            dataKey="views"
            stroke="#267373"
            dot={false}
            strokeWidth={1.75}
          />
        </LineChart>
      </ResponsiveContainer>

      {!hasViews && (
        <Typography className="no-data" variant="body2" color="textSecondary">
          No views recorded
        </Typography>
      )}
    </Box>
  );
}
